import { WorkflowStepCode } from '@exam-management/graphql';
import { APP_PERMISSIONS } from '@exam-management/permissions';
import { useNavigate } from '@exam-management/router';
import { useAuth } from '@shared/auth';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  AssignmentOutlinedIcon,
  Button,
  DescriptionOutlinedIcon,
  Divider,
  ExpandMoreRoundedIcon,
  Flex,
  GroupOutlinedIcon,
  Rotate,
  Text,
} from '@shared/components';
import { useDisclosure } from '@shared/hooks';
import { WithPermission } from '@shared/permissions';
import { formatSecondsToHoursMinutes } from '@shared/utils';
import { useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { AddReviewerButton } from '../../containers/add-reviewer-button';
import { ChangeReviewerModal } from '../../containers/change-reviewer-modal';
import { ConfirmRemoveReviewerModal } from '../../containers/confirm-remove-reviewer-modal';
import { QSetSubtaskMenus } from '../../containers/qset-subtask-menus';
import type { SubtaskDetails } from '../../types';
import { QSetSubtaskCard } from '../qset-subtask-card';
import { StartReviewButton } from '../start-review-button';

export interface QSetSubTaskDetailsAccordionProps {
  taskId: string;
  subTasks: SubtaskDetails[];
  qSetCode: string;
  defaultExpanded?: boolean;
  isAddReviewerAllowed?: boolean;
  isLeadReviewer?: boolean;
  isReviewer?: boolean;
  isTaskOwner?: boolean;
  mainTaskId?: string;
}

export default function QSetSubTaskDetailsAccordion(
  props: Readonly<QSetSubTaskDetailsAccordionProps>,
) {
  const {
    taskId,
    subTasks,
    defaultExpanded,
    qSetCode,
    isAddReviewerAllowed = false,
    isLeadReviewer = false,
    isReviewer = false,
    isTaskOwner = false,
    mainTaskId,
  } = props;

  const [isOpen, { toggle }] = useDisclosure(defaultExpanded);
  const [removeSubtaskId, setRemoveSubtaskId] = useState<string | null>(null);
  const [changeReviewerSubtaskId, setChangeReviewerSubtaskId] = useState<string | null>(null);

  const { userProfile } = useAuth();
  const currentUserId = userProfile?.id ?? '';

  const navigate = useNavigate();
  const { pathname } = useLocation();
  const subtaskRouteTemplate = pathname.startsWith('/my-task')
    ? '/my-task/qset-review/:taskId/subtask/:subTaskId'
    : '/task-management/qset-review/:taskId/subtask/:subTaskId';

  const firstSubTask = subTasks[0] as SubtaskDetails | undefined;

  const { reviewerNames, NoOfReviewers, existingReviewerIds } = useMemo(() => {
    const uniqueNamesSet = new Set<string>();
    const reviewerIds = new Set<string>();

    subTasks.forEach((subTask) => {
      const name = subTask.assignee
        ? `${subTask.assignee.firstname} ${subTask.assignee.lastname}`
        : '-';
      uniqueNamesSet.add(name);

      if (subTask.assignee?.id) {
        reviewerIds.add(subTask.assignee.id);
      }
    });

    const uniqueNames = Array.from(uniqueNamesSet);

    return {
      reviewerNames:
        uniqueNames.length > 3
          ? `${uniqueNames.slice(0, 3).join(', ')}, ...`
          : uniqueNames.join(', '),
      NoOfReviewers: uniqueNames.length,
      existingReviewerIds: Array.from(reviewerIds),
    };
  }, [subTasks]);

  if (!firstSubTask) return null;

  return (
    <Accordion
      disableGutters
      elevation={0}
      expanded={isOpen}
      onChange={toggle}
      sx={(theme) => ({
        border: '1px solid',
        borderColor: theme.palette.grey[300],
        overflow: 'hidden',
        '&.MuiAccordion-root': {
          borderRadius: theme.shape.rounded(2),
        },
      })}
    >
      <AccordionSummary
        component={Flex}
        expandIcon={
          <Rotate in={false}>
            <ExpandMoreRoundedIcon />
          </Rotate>
        }
        sx={(theme) => ({
          px: theme.spacing(4),
          py: theme.spacing(3),
          backgroundColor: isOpen ? theme.palette.grey[50] : 'transparent',
          '& .MuiAccordionSummary-content': { m: theme.spacing(0), alignItems: 'center' },
        })}
      >
        <Flex
          sx={(theme) => ({
            gap: theme.spacing(4),
            width: '100%',
            mr: theme.spacing(3),
            alignItems: 'center',
          })}
        >
          <Flex
            sx={(theme) => ({
              flexDirection: 'column',
              alignItems: 'center',
              borderRadius: '5.5px',
              p: '8.25px',
              backgroundColor: theme.palette.primary.lighter,
              border: '1.375px solid',
              borderColor: theme.palette.primary.light,
              height: theme.spacing(11),
              width: theme.spacing(11),
            })}
          >
            <DescriptionOutlinedIcon
              sx={(theme) => ({
                color: theme.palette.primary.main,
                height: '27.5px',
                width: '27.5px',
              })}
            />
          </Flex>
          <Flex sx={(theme) => ({ flexDirection: 'column', flex: 1, gap: theme.spacing(1) })}>
            <Text variant='h5'>ชุดข้อสอบ: {qSetCode}</Text>
            {isLeadReviewer || isTaskOwner ? (
              <Flex
                sx={(theme) => ({
                  gap: theme.spacing(1),
                  alignItems: 'center',
                })}
              >
                <GroupOutlinedIcon
                  sx={(theme) => ({
                    height: theme.spacing(4),
                    width: theme.spacing(4),
                    color: theme.palette.text.secondary,
                  })}
                />
                <Text
                  variant='body2'
                  sx={(theme) => ({
                    lineHeight: theme.spacing(5),
                    color: theme.palette.text.secondary,
                  })}
                >
                  Reviewer:
                </Text>
                <Text
                  variant='body2'
                  sx={(theme) => ({
                    lineHeight: theme.spacing(5),
                    color: theme.palette.text.secondary,
                  })}
                >
                  {reviewerNames} ({NoOfReviewers} คน)
                </Text>
              </Flex>
            ) : null}
          </Flex>
          <Flex sx={(theme) => ({ gap: theme.spacing(3), alignItems: 'center' })}>
            {isTaskOwner || isLeadReviewer ? (
              <Button
                variant='outlined'
                sx={(theme) => ({
                  py: theme.spacing(1.5),
                  px: theme.spacing(3),
                  borderColor: theme.palette.grey[300],
                  alignItems: 'center',
                  gap: theme.spacing(1.5),
                  height: 'fit-content',
                })}
                onClick={() =>
                  navigate(
                    pathname.startsWith('/my-task')
                      ? `/my-task/qset-review/:taskId/qset/:qSetId/review-result`
                      : `/task-management/qset-review/:taskId/qset/:qSetId/review-result`,
                    {
                      params: { taskId, qSetId: firstSubTask.qsetId },
                    },
                  )
                }
              >
                <AssignmentOutlinedIcon
                  sx={(theme) => ({ height: theme.spacing(5), width: theme.spacing(5) })}
                />
                <Text variant='body2Semibold' sx={(theme) => ({ lineHeight: theme.spacing(5) })}>
                  View Summary
                </Text>
              </Button>
            ) : null}
            {isAddReviewerAllowed ? (
              <WithPermission requiredPermission={[APP_PERMISSIONS.SETTING.USER.VIEW]}>
                <AddReviewerButton
                  excludeUserIds={existingReviewerIds}
                  qsetId={firstSubTask.qsetId}
                />
              </WithPermission>
            ) : null}
            {isReviewer && !isLeadReviewer && mainTaskId
              ? (() => {
                  const targetStatus = subTasks.some(
                    (st) => st.currentWorkflowStep.code === WorkflowStepCode.ReviewQsetTodo,
                  )
                    ? WorkflowStepCode.ReviewQsetTodo
                    : subTasks.some(
                          (st) =>
                            st.currentWorkflowStep.code === WorkflowStepCode.ReviewQsetInReview,
                        )
                      ? WorkflowStepCode.ReviewQsetInReview
                      : subTasks.some(
                            (st) =>
                              st.currentWorkflowStep.code === WorkflowStepCode.ReviewQsetReviewed,
                          )
                        ? WorkflowStepCode.ReviewQsetReviewed
                        : null;
                  if (!targetStatus) return null;
                  const targetSubTask = subTasks.find(
                    (st) => st.currentWorkflowStep.code === targetStatus,
                  );
                  if (!targetSubTask) return null;
                  return (
                    <StartReviewButton
                      mainTaskId={mainTaskId}
                      subTaskId={targetSubTask.id}
                      subtaskStatus={targetStatus}
                    />
                  );
                })()
              : null}
          </Flex>
        </Flex>
      </AccordionSummary>
      <Divider
        sx={(theme) => ({
          borderColor: theme.palette.grey[300],
        })}
      />
      <AccordionDetails
        sx={(theme) => ({
          p: theme.spacing(4),
        })}
      >
        <Flex
          sx={(theme) => ({
            flexDirection: 'column',
            gap: theme.spacing(4),
          })}
        >
          <Flex>
            <Flex
              sx={(theme) => ({
                gap: theme.spacing(4),
              })}
            >
              <Flex
                sx={(theme) => ({
                  gap: theme.spacing(2),
                })}
              >
                <Text variant='body2'>QConfig ID: </Text>
                <Text variant='body2Semibold'>{firstSubTask.qConfigCode}</Text>
              </Flex>
              <Divider orientation='vertical' flexItem />
              <Flex
                sx={(theme) => ({
                  gap: theme.spacing(2),
                })}
              >
                <Text variant='body2'>จำนวนข้อสอบ:</Text>
                <Text variant='body2Semibold'>{firstSubTask.totalQuestions}</Text>
              </Flex>
              <Divider orientation='vertical' flexItem />
              <Flex
                sx={(theme) => ({
                  gap: theme.spacing(2),
                })}
              >
                <Text variant='body2'>ระยะเวลาทำข้อสอบ: </Text>
                <Text variant='body2Semibold'>
                  {firstSubTask.timeLimits[0]?.value
                    ? formatSecondsToHoursMinutes(firstSubTask.timeLimits[0].value, 'TEXT')
                    : '-'}
                </Text>
              </Flex>
            </Flex>
          </Flex>
          {subTasks.map((subTask) => (
            <QSetSubtaskCard
              key={subTask.id}
              subtask={subTask}
              onClick={() =>
                navigate(subtaskRouteTemplate, {
                  params: {
                    taskId,
                    subTaskId: subTask.id,
                  },
                })
              }
              isOwnerOrLeadReviewer={isTaskOwner || isLeadReviewer}
              endAdornment={
                isTaskOwner || isLeadReviewer ? (
                  <QSetSubtaskMenus
                    workflowStepCode={subTask.currentWorkflowStep.code}
                    onRemoveReviewer={() => setRemoveSubtaskId(subTask.id)}
                    onChangeReviewer={() => setChangeReviewerSubtaskId(subTask.id)}
                    subtaskId={subTask.id}
                    shouldShowRemoveReviewer={subTasks.length > 1}
                    isAssignedToSubtask={subTask.assignee?.id === currentUserId}
                  />
                ) : null
              }
            />
          ))}
        </Flex>
      </AccordionDetails>
      {removeSubtaskId ? (
        <ConfirmRemoveReviewerModal
          open
          subtaskId={removeSubtaskId}
          onClose={() => setRemoveSubtaskId(null)}
        />
      ) : null}
      {changeReviewerSubtaskId ? (
        <ChangeReviewerModal
          open
          subtaskId={changeReviewerSubtaskId}
          excludeUserIds={existingReviewerIds}
          onClose={() => setChangeReviewerSubtaskId(null)}
        />
      ) : null}
    </Accordion>
  );
}
